import React, { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import type { AiReportInput } from '../../global';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Textarea } from '../../components/ui/Input';
import { Switch } from '../../components/ui/Switch';
import { cn } from '../../components/ui/cn';
import { useLanguage } from '../../lib/i18n';

type Reason = AiReportInput['reason'];

/** Bildirilen içerik: pencereyi açan yer verir. */
export interface AiReportTarget {
  surface: AiReportInput['surface'];
  /** Yanıtın ya da özetin metni — yalnızca kullanıcı "metni ekle"yi açarsa gider. */
  content: string;
  prompt?: string;
  meetingId?: string;
}

interface ReportAiDialogProps {
  /** null: kapalı. */
  target: AiReportTarget | null;
  onClose: () => void;
}

const NOTE_MAX = 1000;

/**
 * Yapay zekâ içeriğini bildirme penceresi (Sohbet yanıtı, toplantı özeti, kayıt
 * sırasındaki soru-cevap). Google Play'in yapay zekâ içeriği politikası
 * uygunsuz içeriğin uygulamadan çıkmadan bildirilebilmesini istiyor.
 *
 * Ekibimiz toplantı içeriklerini göremiyor (şifreli); bu yüzden yanıtın metni
 * varsayılan olarak gitmez, kullanıcı anahtarı açarsa eklenir.
 */
export const ReportAiDialog: React.FC<ReportAiDialogProps> = ({ target, onClose }) => {
  const { t } = useLanguage();
  const [reason, setReason] = useState<Reason | null>(null);
  const [note, setNote] = useState('');
  const [includeContent, setIncludeContent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Her yeni bildirim temiz başlasın.
  useEffect(() => {
    if (!target) return;
    setReason(null);
    setNote('');
    setIncludeContent(false);
    setSent(false);
    setError(null);
  }, [target]);

  const reasons: Array<{ id: Reason; label: string }> = [
    { id: 'offensive', label: t('Rahatsız edici ya da uygunsuz', 'Offensive or inappropriate') },
    { id: 'harmful', label: t('Zararlı ya da tehlikeli', 'Harmful or dangerous') },
    { id: 'inaccurate', label: t('Yanlış ya da uydurma bilgi', 'Inaccurate or made up') },
    { id: 'other', label: t('Başka bir sorun', 'Something else') },
  ];

  const isSummary = target?.surface === 'summary';

  const submit = async () => {
    if (!target || !reason || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      await window.zabit.reportAiContent({
        surface: target.surface,
        reason,
        note: note.trim() || undefined,
        ...(includeContent ? { content: target.content, prompt: target.prompt } : {}),
        meetingId: target.meetingId,
      });
      setSent(true);
    } catch (err) {
      setError((err as Error).message || t('Bildirim gönderilemedi.', 'Could not send the report.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      title={isSummary ? t('Özeti bildir', 'Report summary') : t('Yanıtı bildir', 'Report response')}
      className="max-w-md"
      footer={
        sent ? (
          <Button onClick={onClose}>{t('Kapat', 'Close')}</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('Vazgeç', 'Cancel')}
            </Button>
            <Button onClick={() => void submit()} disabled={!reason || isSending}>
              {isSending && <Loader2 className="size-4 animate-spin" />}
              {t('Bildir', 'Report')}
            </Button>
          </>
        )
      }
    >
      {sent ? (
        <div className="flex items-start gap-2.5 text-sm leading-relaxed">
          <Check className="size-5 shrink-0 text-emerald-500" />
          <p>
            {t(
              'Teşekkürler, bildirimin ekibimize ulaştı. Yapay zekâ yanıtlarını iyileştirmek için inceliyoruz.',
              'Thanks, your report reached our team. We review reports to improve AI responses.'
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-4 text-sm leading-relaxed">
          <p className="text-muted-foreground">
            {isSummary
              ? t(
                  'Bu özet yapay zekâyla üretildi. Uygunsuz, zararlı ya da yanlış bir şey gördüysen bize bildir.',
                  'This summary was generated by AI. If you saw something inappropriate, harmful or wrong, let us know.'
                )
              : t(
                  'Bu yanıt yapay zekâyla üretildi. Uygunsuz, zararlı ya da yanlış bir şey gördüysen bize bildir.',
                  'This response was generated by AI. If you saw something inappropriate, harmful or wrong, let us know.'
                )}
          </p>

          <div role="radiogroup" aria-label={t('Gerekçe', 'Reason')} className="space-y-1.5">
            {reasons.map((option) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={reason === option.id}
                onClick={() => setReason(option.id)}
                className={cn(
                  'w-full flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors',
                  reason === option.id ? 'border-brand bg-brand-soft' : 'border-border hover:bg-muted/50'
                )}
              >
                <span
                  className={cn(
                    'size-4 shrink-0 rounded-full border-2 flex items-center justify-center',
                    reason === option.id ? 'border-brand' : 'border-muted-foreground/50'
                  )}
                >
                  {reason === option.id && <span className="size-2 rounded-full bg-brand" />}
                </span>
                {option.label}
              </button>
            ))}
          </div>

          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value.slice(0, NOTE_MAX))}
            placeholder={t('Ne oldu? (isteğe bağlı)', 'What happened? (optional)')}
            aria-label={t('Açıklama', 'Details')}
            rows={3}
          />

          <div className="flex items-start gap-3 rounded-xl border border-border px-3.5 py-3">
            <div className="flex-1">
              <div className="font-medium">{isSummary ? t('Özetin metnini ekle', 'Include the summary text') : t('Yanıtın metnini ekle', 'Include the response text')}</div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t(
                  'Kapalıyken yalnızca gerekçen ve notun gider; ekibimiz toplantılarının içeriğini göremez. Açarsan bu metin yalnızca bu bildirimi incelemek için okunur ve 90 gün sonra silinir.',
                  'When off, only your reason and note are sent; our team can’t see your meetings. If you turn it on, this text is read only to review this report and is deleted after 90 days.'
                )}
              </p>
            </div>
            <Switch
              checked={includeContent}
              onChange={setIncludeContent}
              label={isSummary ? t('Özetin metnini ekle', 'Include the summary text') : t('Yanıtın metnini ekle', 'Include the response text')}
            />
          </div>

          {error && <p className="text-rose-500">{error}</p>}
        </div>
      )}
    </Dialog>
  );
};
